// لوحة «مدير إدارة» — نفس هوية V2 (زجاج داكن + ذهبي)
import { createContext, useContext, useState } from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, ROLE_AR } from "@/v2/lib/rbac";
import { Chip, GlassCard, Skeleton } from "@/v2/admin/ui";
import {
  Building2, LayoutDashboard, Users, Trophy, BarChart3, FileText, Megaphone, Swords,
} from "lucide-react";

const SECTIONS = [
  { to: "/dept", label: "لوحة الإدارة", icon: LayoutDashboard, end: true },
  { to: "/dept/employees", label: "موظفو إدارتي", icon: Users },
  { to: "/dept/leaderboard", label: "متصدرو الإدارة", icon: Trophy },
  { to: "/dept/competition", label: "تحدي الإدارات", icon: Swords },
  { to: "/dept/analytics", label: "التحليلات", icon: BarChart3 },
  { to: "/dept/reports", label: "تقارير الإدارة", icon: FileText },
  { to: "/dept/motivate", label: "التحفيز والنبض", icon: Megaphone },
];

const Ctx = createContext({ q: "", department: "", meta: null });
export const useDept = () => useContext(Ctx);

export const downloadDeptPdf = async (body, filename) => {
  const res = await api.post("/dept/reports/pdf", body, { responseType: "blob" });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

export default function DeptShell() {
  const { user } = useAuth();
  const allowed = can(user, "view_dept") || can(user, "manage_users");
  const isMgr = user?.role === "dept_manager";
  const [department, setDepartment] = useState("");

  const { data: depts = [] } = useQuery({
    queryKey: ["dept-all"],
    queryFn: async () => (await api.get("/admin/departments")).data,
    enabled: allowed && !isMgr,
  });
  const { data: meta, isLoading, error } = useQuery({
    queryKey: ["dept-meta", department],
    queryFn: async () => (await api.get(`/dept/meta?department=${encodeURIComponent(department)}`)).data,
    enabled: allowed,
  });

  if (!allowed) return <Navigate to="/dashboard" replace />;
  const q = department ? `department=${encodeURIComponent(department)}` : "";

  return (
    <div className="space-y-6" data-testid="dept-center">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#bb9661]/15 text-[#e3c799] flex items-center justify-center">
            <Building2 size={22} />
          </div>
          <div>
            <div className="text-lg font-extrabold" data-testid="dept-name">
              {meta?.department ? `إدارة ${meta.department}` : "لوحة الإدارة"}
            </div>
            <div className="text-xs text-[#bcc8cf]">متابعة كاملة لموظفي إدارتك وأدائهم وتقاريرهم</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isMgr && (
            <select value={department} onChange={(e) => setDepartment(e.target.value)}
              data-testid="dept-picker" className="v2-input !w-auto text-xs font-bold">
              <option value="">إدارتي الحالية</option>
              {depts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          )}
          <Chip tone="gold" className="!py-1.5">{ROLE_AR[user?.role] || ""} — {user?.name}</Chip>
        </div>
      </div>

      <nav className="flex lg:flex-wrap gap-2 overflow-x-auto lg:overflow-visible pb-1" data-testid="dept-nav">
        {SECTIONS.map((s) => (
          <NavLink key={s.to} to={s.to} end={s.end} data-testid={`dept-nav-${s.to.split("/").pop()}`}
            className={({ isActive }) =>
              `shrink-0 inline-flex items-center gap-2 px-4 h-10 rounded-full text-xs sm:text-sm font-bold transition-all ${
                isActive
                  ? "bg-gradient-to-l from-[#e3c799] to-[#bb9661] text-[#25343f]"
                  : "text-[#bcc8cf] bg-white/5 border border-white/10 hover:text-white"
              }`}>
            <s.icon size={16} /> {s.label}
          </NavLink>
        ))}
      </nav>

      {isLoading ? <Skeleton className="h-72 w-full" /> : error ? (
        <GlassCard className="p-10 text-center text-[#F7A08A] text-sm" data-testid="dept-error">
          {error.response?.data?.detail || "تعذّر تحميل بيانات الإدارة."}
        </GlassCard>
      ) : (
        <Ctx.Provider value={{ q, department, meta }}>
          <Outlet />
        </Ctx.Provider>
      )}
    </div>
  );
}
